import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Input, useAlert } from "@digital-services/ui";

import { useUpdateOrganization } from "@organization/services/use-organization-update";
import { maxLength } from "@validators/MaxLengthValidator";
import { isNotEmpty } from "@validators/NotEmptyValidator";
import { isNotRegExMatch } from "@validators/RegExValidator";

import { useInput } from "@hooks/useInput";
import { useOrganizationContext } from "@hooks/useOrganizationContext";

interface Props {
  label: string;
  value: string | undefined;
}

const OrganizationName: FunctionComponent<Props> = ({ label, value }) => {
  const { t } = useTranslation();

  const [initialValue, setInitialValue] = useState(value);
  const [, setIsUpdating] = useState(false);
  const { notifyError } = useAlert();
  const { updateName } = useUpdateOrganization();
  const { refresh: refreshUserContext } = useOrganizationContext();

  const {
    value: organizationName,
    isValid,
    errorMessage,
    valueChangeHandler,
    reset
  } = useInput(
    [
      maxLength(256, () =>
        t("validation.string.max-length", {
          fieldName: label,
          maxLength: 256
        })
      ),
      isNotEmpty(() => t("validation.string.not-empty", { fieldName: label })),
      isNotRegExMatch(/[<>]/, () =>
        t("validation.string.illegal-characters", {
          fieldName: label,
          characters: "<, >"
        })
      )
    ],
    initialValue
  );

  // Ability to edit Organization Name on Customer portal has been disabled for all users.
  // More details here:
  // https://dev.azure.com/abbevci/Team.Falcon/_workitems/edit/251650
  // To be confirmed in future if to remove this code entirely
  const saveChanges = async () => {
    if (!isValid || organizationName === initialValue) return;

    setIsUpdating(true);

    const { isSuccess } = await updateName(organizationName);

    if (isSuccess) {
      setInitialValue(organizationName);
      refreshUserContext();
    } else {
      reset();
      notifyError({
        header: t("not_saved")
      });
    }

    setIsUpdating(false);
  };

  return (
    <Input
      data-testid="organization-name"
      autoComplete="organization" // string value should be standard HTML value
      id="name"
      type="text"
      label={label}
      error={!isValid}
      disabled={true} // if after long time of disabled edit, need to remove more code from this file in future
      helperText={isValid ? undefined : errorMessage}
      value={organizationName}
      onChange={event => {
        valueChangeHandler(event.target.value);
      }}
      onBlur={saveChanges}
    />
  );
};

export default OrganizationName;
